<template>
  <LayoutBaseContent class="px-2 mt-2">
    <div class="flex justify-between items-center mb-4 sm:mb-2 relative">
      <h1 class="font-medium text-xl mb-0 hidden sm:block">
        <span v-if="$route.name !== 'search'">
          <span class="hidden xs:inline">{{ $t('search.searchTitle') }}</span>
          {{ compTitle }}
        </span>

        <span v-else>{{ $t('search.searchAll') }}</span>
      </h1>

      <div class="flex items-center w-full sm:w-auto justify-between">
        <UtilSearchLayoutToggle v-if="!$breakpoint.sm" class="mr-2" />

        <UtilButton v-if="$breakpoint.mdAndDown" @click="showModalFilters = true" color="primary">
          <FontAwesomeIcon :icon="faSortAmountUp" />
          {{ $t('buttons.sortAndFilter') }}
        </UtilButton>
      </div>
    </div>

    <div v-if="$breakpoint.mdAndDown" class="flex items-center">
      <UtilSearchLayoutToggle v-if="$breakpoint.sm" class="mb-2 ml-2" />
    </div>

    <div v-if="$route.meta.category && $route.meta.category.shortDescription" class="mb-6">
      <div v-html="$route.meta.category.shortDescription" class="blog-content text-lg" />
    </div>

    <!-- Show currently active filters chips -->
    <div v-if="Object.keys(activeFilters).length > 0" class="-mt-2 mb-2 align-middle active-filters">
      <div class="mr-2 mb-2 hidden md:inline-block">{{ $t('search.appliedFilters') }}:</div>

      <UtilTag
        v-if="compIsSearchingAroundGeo"
        key="current-search-distance"
        @click="$store.dispatch('search/resetGeoDistance')"
        class="inline-block bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
      >
        <span class="mr-1">
          {{ `${distanceToHuman(search.aroundRadius)} of ${search.aroundPostcode}` }}
        </span>

        <FontAwesomeIcon :icon="faTimes" />
      </UtilTag>

      <UtilTag
        v-if="search.index !== defaultSearchIndex"
        key="current-search-orderby"
        @click="$store.dispatch('search/mutateAndApply', { key: 'index', value: defaultSearchIndex })"
        class="inline-block bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
      >
        <span class="mr-1">{{ $t('search.orderBy') }} {{ orderLabel }}</span>

        <FontAwesomeIcon :icon="faTimes" />
      </UtilTag>

      <UtilTag
        v-if="search.query"
        key="current-search-query"
        @click="$store.dispatch('search/mutateAndApply', { key: 'query', value: '' })"
        class="inline-block bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
      >
        <span class="mr-1">{{ search.query }}</span>
        <FontAwesomeIcon :icon="faTimes" />
      </UtilTag>

      <UtilTextLink v-if="search.geoCountry" :to="`/${search.commodity}`" class="no-underline inline-block">
        <UtilTag
          key="current-search-geo-filter"
          class="bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
        >
          <span v-if="search.geoRegion" class="mr-1">{{ search.geoRegion }}</span>

          <span class="mr-1">{{ search.geoCountry }}</span>

          <FontAwesomeIcon :icon="faTimes" />
        </UtilTag>
      </UtilTextLink>

      <UtilTextLink v-if="$route.meta.geoNation" to="/livestock" class="no-underline inline-block">
        <UtilTag
          key="current-search-geo-filter"
          class="bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
        >
          <span class="mr-1">{{ $route.meta.geoNation }}</span>

          <FontAwesomeIcon :icon="faTimes" />
        </UtilTag>
      </UtilTextLink>

      <UtilTextLink v-if="search.popularTag" :to="`/${search.commodity}`" class="no-underline inline-block">
        <UtilTag
          key="current-search-popular-filter"
          class="bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
        >
          <span class="mr-1">{{ search.popularTag }}</span>

          <FontAwesomeIcon :icon="faTimes" />
        </UtilTag>
      </UtilTextLink>

      <UtilTextLink v-if="$route.params.lvl0" :to="`/${search.commodity}`" class="no-underline inline-block">
        <UtilTag
          key="current-search-popular-filter"
          class="bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
        >
          <span class="mr-1">{{ $route.params.lvl0 }}</span>

          <span v-if="$route.params.lvl1" class="mr-1">{{ `> ${$route.params.lvl1}` }}</span>

          <FontAwesomeIcon :icon="faTimes" />
        </UtilTag>
      </UtilTextLink>

      <template v-if="activeFilters.facets">
        <UtilTag
          v-for="facet in activeFilters.facets.slice(0, facetFilterVisibleLimit)"
          :key="[facet.key, facet.value].join(':')"
          @click="$store.dispatch('search/toggleFacet', { key: facet.key, value: facet.value })"
          class="inline-block bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
        >
          <span class="flex items-center">
            <span v-if="typeof facet.value === 'string' && facet.value.includes(' > ')" class="mr-1">
              {{ facet.value.split(' > ')[1] }}
            </span>

            <span v-else-if="getAdditionalFacets(compCommodity, searchIs)[facet.key]" class="mr-1">
              {{ getAdditionalFacets(compCommodity, searchIs)[facet.key].label }}
            </span>

            <span v-else-if="getCustomFacet(facet.key, facet.value)" class="mr-1">
              {{ getCustomFacet(facet.key, facet.value).label }}
            </span>

            <span v-else class="mr-1">{{ facet.value }}</span>

            <FontAwesomeIcon :icon="faTimes" />
          </span>
        </UtilTag>

        <UtilTextLink
          v-if="activeFilters.facets.slice(0, facetFilterVisibleLimit).length < activeFilters.facets.length"
          @click="facetFilterVisibleLimit = 100"
          class="ml-2 text-brand text-sm block sm:inline-block"
        >
          and
          {{ activeFilters.facets.length - activeFilters.facets.slice(0, facetFilterVisibleLimit).length }} more
          filters
        </UtilTextLink>
      </template>

      <UtilTag
        v-if="$route.query.page && $route.query.page > 1"
        @click="$store.dispatch('search/changePage', 1)"
        class="inline-block bg-gray-darker text-white cursor-pointer hover:bg-gray mb-2"
      >
        <span class="mr-1">Page {{ $route.query.page }}</span>
        <FontAwesomeIcon :icon="faTimes" />
      </UtilTag>
    </div>

    <div class="flex">
      <!-- Sidebar filters -->
      <div v-if="$breakpoint.lgAndUp" class="flex-shrink-0 mr-2 hidden md:block" style="width: 185px">
        <SearchFilters :commodity="compCommodity" />
      </div>

      <!-- Hit Card Results -->
      <div class="hit-results-wrapper">
        <div class="sm:absolute px-2 mb-8 z-5">
          <WidgetAdvert shape="banner" isExpandable :advertIndex="1" />
        </div>

        <div class="flex sm:mt-64">

          <div class="flex-grow md:w-6/12 xl:w-8/12">
            <!-- LOADING -->
            <div
              v-if="!listings || search.loading"
              class="hit-results"
              :class="{ 'hit-results-list': search.layout === 'list' }"
            >
              <div
                v-for="i in search.hitsPerPage"
                :key="i"
                class="hit-wrapper"
                :class="{ 'hit-wrapper-list': search.layout === 'list' }"
              >
                <UtilPlaceholderHitCardList v-if="search.layout === 'list'" />

                <UtilPlaceholderHitCard v-else />
              </div>
            </div>

            <!-- NO RESULTS -->
            <div
              v-else-if="!compHasListings"
              class="container-padding h-full w-full flex relative text-center h-full overflow-visible"
            >
              <div class="sticky mx-auto mb-auto" style="top: 20px">
                <h3 class="font-medium text-3xl text-gray uppercase">{{ $t('search.noResults') }}</h3>

                <p class="text-gray-darker mt-2">
                  {{ $t('search.noResultsBroaden') }}
                </p>

                <UtilMessage class="mt-6 text-left">
                  <i18n path="search.cantFindLookingFor" tag="span">
                    <UtilTextLink slot="createWantedListing" to="/post/listing/new" class="font-medium">
                      <span>
                        <br class="sm:hidden" />
                        {{ $t('search.createWantedListing') }}
                      </span>
                    </UtilTextLink>
                  </i18n>
                </UtilMessage>
              </div>
            </div>

            <div v-else class="hit-results" :class="{ 'hit-results-list': search.layout === 'list' }">
              <!-- Portal Card -->

              <div v-if="compMatchingPortal && compMatchingPortal.name" class="hit-wrapper">
                <div class="rounded bg-white flex flex-col items-center h-full">
                  <UtilImage
                    @click="portalCardClick()"
                    :fileRef="compMatchingPortal.bannerFileRef"
                    cover
                    showOriginal
                    roundedTop
                    class="h-64 w-full cursor-pointer"
                  />

                  <div class="flex flex-col flex-grow items-center justify-between text-center p-4">
                    <h4 @click="portalCardClick()" class="font-medium text-2xl cursor-pointer">
                      {{ compMatchingPortal.name }}
                    </h4>

                    <p v-if="compMatchingPortal.description" class="mt-2 clamp-3">
                      {{ compMatchingPortal.description.trim().slice(0, 300) }}
                    </p>

                    <UtilButton @click="portalCardClick()" color="secondary" class="mt-4 inline-block">
                      View Breed Society
                    </UtilButton>

                    <p class="mt-2 text-xs px-4">
                      See listings from certified members of
                      <br />
                      {{ compMatchingPortal.name }}
                    </p>
                  </div>
                </div>
              </div>

              <template v-for="(item, index) in listings">
                <template v-if="$breakpoint.smAndUp">
                  <WidgetAdvert
                    v-if="
                      // TODO: This logic makes no sense. We are trying to inject at the end of every 4th row
                      index > 0 && (((index - 2) % 12 === 0 && index > 3) || index === 3) && compIsAdvertsAvailable
                    "
                    :key="`advert-${index}`"
                    :advertIndex="index < 5 ? 0 : 5"
                    :shape="search.layout === 'list' ? 'banner' : 'square'"
                    class="hit-wrapper flex flex-col items-center justify-center"
                    :class="{ 'hit-wrapper-list': search.layout === 'list' }"
                  />
                </template>

                <template v-else>
                  <WidgetAdvert
                    v-if="
                      // TODO: This logic makes no sense. We are trying to inject at the end of every 4th row
                      index > 0 && (((index - 2) % 12 === 0 && index > 3) || index === 3) && compIsAdvertsAvailable
                    "
                    :key="`advert-${index}`"
                    :advertIndex="index < 5 ? 0 : 5"
                    shape="skyscraper"
                    class="hit-wrapper flex flex-col items-center justify-center"
                    :class="{ 'hit-wrapper-list': search.layout === 'list' }"
                  />
                </template>

                <div
                  :key="item.objectID"
                  class="hit-wrapper"
                  :class="{ 'hit-wrapper-list': search.layout === 'list' }"
                >
                  <UtilSearchHit :layout="search.layout" :hit="item" showStatusTags />
                </div>
              </template>
            </div>

            <UtilDivider v-if="compHasListings" class="mx-2 my-4" />

            <UtilPagination
              v-if="compHasListings"
              @changePage="changePage"
              @changeHitsPerPage="changeHitsPerPage"
              :hitsPerPage="search.hitsPerPage"
              :currentPage="search.page"
              :totalPages="search.result ? search.result.nbPages : 0"
              :totalResults="search.result ? search.result.nbHits : 0"
              :generateUrl="generateUrl"
              :maxVisiblePages="$breakpoint.mdAndDown ? 4 : 7"
            />

            <div v-if="compHasListings" class="absolute bottom-0 w-full md:text-center mb-4">
              <div>
                <FontAwesomeIcon :icon="faInfoCircle" class="mr-1" />

                <i18n path="search.cantFindLookingFor" tag="span">
                  <UtilTextLink slot="createWantedListing" to="/post/listing/new" class="font-medium">
                    <span>
                      <br class="sm:hidden" />
                      {{ $t('search.createWantedListing') }}
                    </span>
                  </UtilTextLink>
                </i18n>
              </div>
            </div>
          </div>

          <div v-if="$breakpoint.mdAndUp" class="flex-none">
            <WidgetAdvert shape="skyscraper" :advertIndex="1" class="inline-block sticky top-10  md:ml-4 lg:ml-8" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="$route.meta.category && $route.meta.category.description">
      <UtilDivider class="mx-2 my-4" />

      <div v-html="$route.meta.category.description" class="blog-content text-lg" />
    </div>

    <ModalBase
      v-if="$breakpoint.mdAndDown"
      v-show="showModalFilters"
      @close="showModalFilters = false"
      name="search-filters"
      :title="$t('titles.filterSortSearchResults')"
    >
      <template #body>
        <SearchFilters />
      </template>

      <template #footer>
        <div class="flex items-center justify-between">
          <div class="w-5/12 mr-4">
            <UtilButton @click="resetFiltersModal()" color="secondary">
              {{ $t('buttons.resetFilters') }}
            </UtilButton>
          </div>

          <UtilButton color="primary" @click="showModalFilters = false" block>
            {{ $tc('buttons.showResults', search.result ? search.result.nbHits : 0) }}
          </UtilButton>
        </div>
      </template>
    </ModalBase>
  </LayoutBaseContent>
</template>

<script>
  import {faInfoCircle, faLongArrowRight, faSortAmountUp, faTimes} from '@fortawesome/pro-regular-svg-icons'
  import {faCaretDown, faCaretUp} from '@fortawesome/pro-solid-svg-icons'
  import LegacyApi from '@/lib/api/legacy'
  import {mapGetters, mapState} from 'vuex'
  import {get, isEmpty, startCase} from 'lodash'
  import {defaultSearchIndex, disjunctiveFacets, facetMapping} from '@/store/modules/search.module'
  import {generateMetaTags} from '@/lib/generate-meta'
  import {allSpecies, findCommodityBySlug, findCommodityBySpecies} from '@/lib/helpers/commodities'
  import {anyDistanceValue, distanceToHuman} from '@/lib/options/distance'
  import displayLocation from '@/lib/mapping/display-location'
  import getAdditionalFacets from '@/lib/mapping/search-additional-facets'
  import getCustomFacet from '@/lib/mapping/search-custom-facet'
  import UtilSearchLayoutToggle from '@/components/utils/search/UtilSearchLayoutToggle'
  import UtilPlaceholderHitCard from '@/components/utils/placeholders/UtilPlaceholderHitCard'
  import UtilPlaceholderHitCardList from '@/components/utils/placeholders/UtilPlaceholderHitCardList'
  import UtilMessage from '@/components/utils/UtilMessage'
  import UtilTextLink from '@/components/utils/UtilTextLink'
  import ModalBase from '@/components/modals/ModalBase'
  import WidgetAdvert from '@/components/widgets/WidgetAdvert'
  import UtilButton from '@/components/utils/UtilButton'
  import UtilTag from '@/components/utils/UtilTag'
  import UtilDivider from '@/components/utils/UtilDivider'
  import UtilImage from '@/components/utils/UtilImage'
  import LayoutBaseContent from '@/components/layout/LayoutBaseContent'
  import UtilSearchHit from '@/components/utils/search/UtilSearchHit'
  import UtilPagination from '@/components/utils/UtilPagination'
  import SearchFilters from '@/views/SearchFilters'
  import searchOrder from '@/lib/mapping/search-order'

  export default {
  name: 'Search',
  components: {
    UtilSearchLayoutToggle,
    UtilPlaceholderHitCard,
    UtilPlaceholderHitCardList,
    UtilMessage,
    UtilTextLink,
    ModalBase,
    WidgetAdvert,
    UtilButton,
    UtilTag,
    UtilDivider,
    UtilImage,
    LayoutBaseContent,
    UtilSearchHit,
    UtilPagination,
    SearchFilters
  },
  data() {
    return {
      searchPresetApi: new LegacyApi(),
      showModalFilters: false,
      facetFilterVisibleLimit: 5,
      distanceToHuman,
      getAdditionalFacets,
      getCustomFacet,
      faCaretDown,
      faCaretUp,
      faSortAmountUp,
      faTimes,
      faInfoCircle,
      faLongArrowRight,
      defaultSearchIndex
    }
  },
  watch: {
    '$route.fullPath'(newPath) {
      if (!newPath.includes('menu=')) {
        this.handleRouteChange()
      }
    }
  },
  created() {
    this.setup()
  },
  computed: {
    ...mapState({
      search: state => state.search
    }),

    ...mapGetters({
      searchIs: 'search/searchIs',
      listings: 'search/listings',
      generateUrl: 'search/generateUrl',
      activeFilters: 'search/activeFilters',
      paidBreedSocietyPortals: 'portals/getPaidBreedSocietyPortals'
    }),

    compHasListings() {
      return this.listings && this.listings.length > 0
    },

    compCommodity() {
      return this.search.species === 'cattle'
        ? findCommodityBySlug(this.search.childCommodity)
        : findCommodityBySpecies(this.search.species)
    },

    compIsSearchingAroundGeo() {
      return this.search.aroundLatLng !== '' && parseInt(this.search.aroundRadius) !== anyDistanceValue
    },

    compIsAdvertsAvailable() {
      return this.search.layout === 'list'
        ? this.$store.state.adverts.available.banner.length > 0
        : this.$store.state.adverts.available.square.length > 0
    },

    compVisibleActiveFacetFilters() {
      if (this.facetFilterVisibleLimit) {
        return [...this.activeFilters.facets].slice(0, this.facetFilterVisibleLimit)
      } else {
        return [...this.activeFilters.facets]
      }
    },

    // If a single breed is selected and it matches a portal then show a banner for that portal
    compMatchingPortal() {
      if (this.search.facetRefinementList['categories.lvl1'].length !== 1) {
        return false
      }

      let breed = this.search.facetRefinementList['categories.lvl1'][0].split(' > ')[1]

      let matchingPortal = this.paidBreedSocietyPortals.filter(portal => {
        return portal.slug === breed.toLowerCase()
      })

      if (!matchingPortal.length) {
        return false
      }

      return matchingPortal[0]
    },

    compTitle() {
      if (this.$route.meta.title) {
        return this.$route.meta.title
      }

      if (this.search.childCommodity) {
        return findCommodityBySlug(this.search.childCommodity).name
      }

      if (this.search.popularTag) {
        return startCase(this.search.popularTag)
      }

      if (this.search.geoRegion) {
        return `${startCase(this.search.commodity)} in ${startCase(this.search.geoRegion)}`
      }

      if (this.search.geoCountry) {
        return `${startCase(this.search.commodity)} in ${startCase(this.search.geoCountry)}`
      }

      if (this.search.commodity) {
        return startCase(this.search.commodity)
      }

      return this.$t('search.everything')
    },

    orderLabel() {
      const order = Object.values(searchOrder).find(obj => {
        return obj.value === this.search.index
      })

      return order ? order.text : ''
    }
  },
  methods: {
    async setup() {
      // Add algolia search helper events
      this.search.helper.on('result', ({ results }) => {
        this.$store.commit('search/mutate', { key: 'result', value: results })
        this.$store.commit('search/mutate', { key: 'loading', value: false })
      })

      this.search.helper.on('error', ({ error }) => {
        this.$log.debug('Search error', error)
      })

      this.handleRouteChange()
    },

    // Read params from URL and apply to search store
    async handleRouteChange() {
      let filterChanges = []

      if (this.$route.meta.commodity !== this.search.commodity) {
        this.$store.dispatch('categories/get', this.$route.meta.commodity)
      }

      filterChanges.push({ key: 'popularTag', value: '' })

      // Popular URLs from the sitemap page
      if (this.$route.meta.popular) {
        try {
          // We need to find out what species and meta are set in this search preset
          await this.searchPresetApi.post('listings.searchpresets', {
            commodity: this.$route.meta.commodity,
            preset: this.$route.params.tag
          })

          let species = ''

          if (get(this.searchPresetApi, 'data[0].categories.lvl0[0]')) {
            species = this.searchPresetApi.data[0].categories.lvl0[0].toLowerCase()
          } else if (get(this.searchPresetApi, 'data[0].categories.lvl1[0]')) {
            // From "Sheep > Hill Radnor" we need "sheep"
            species = this.searchPresetApi.data[0].categories.lvl1[0].split(' >')[0].toLowerCase()
          }

          if (species) {
            filterChanges.push({ key: 'species', value: species })

            if (species !== 'cattle') {
              filterChanges.push({ key: 'childCommodity', value: species })
            }
          }
        } catch (error) {
          this.$log.debug('Error fetching search preset', error)
        }

        filterChanges.push({
          key: 'filters',
          value: `${this.$route.params.tag}`
        })

        filterChanges.push({ key: 'popularTag', value: this.$route.params.tag })
      } else {
        // Geo SEO search from sitemap? Include country and region if available
        if (this.$route.meta.geo) {
          let geoFilterString = `${this.$route.meta.algolia} AND location.country:'${startCase(
            this.$route.params.country
          )}'`

          if (this.$route.params.region) {
            geoFilterString += ` AND location.region:'${startCase(this.$route.params.region)}'`
          }

          filterChanges.push({
            key: 'filters',
            value: geoFilterString
          })
        } else {
          filterChanges.push({ key: 'filters', value: this.$route.meta.algolia })
        }

        filterChanges.push({ key: 'childCommodity', value: this.$route.meta.childCommodity })

        let querySpecies = this.$route.query.species ? this.$route.query.species.toLowerCase() : ''
        let species = this.$route.meta.species || querySpecies
        filterChanges.push({ key: 'species', value: species })
      }

      const facetRefinementList = {}

      Object.keys(facetMapping).forEach(facetCustomKey => {
        // disjunctiveFacets are arrays
        if (disjunctiveFacets.includes(facetMapping[facetCustomKey])) {
          facetRefinementList[facetMapping[facetCustomKey]] =
            (this.$route.query[facetCustomKey] && this.$route.query[facetCustomKey].split('~')) || []
        } else {
          // We need to convert "true" into boolean true but maintain other normal strings
          let value = this.$route.query[facetCustomKey]

          if (typeof value !== 'undefined') {
            if (value === 'true') {
              value = true
            }

            if (value === 'false') {
              value = false
            }

            facetRefinementList[facetMapping[facetCustomKey]] = value
          }
        }
      })

      filterChanges.push({ key: 'facetRefinementList', value: facetRefinementList })

      filterChanges.push({ key: 'commodity', value: this.$route.meta.commodity })
      filterChanges.push({ key: 'geoCountry', value: this.$route.params.country })
      filterChanges.push({ key: 'geoRegion', value: this.$route.params.region })

      filterChanges.push({ key: 'query', value: this.$route.query.query })
      filterChanges.push({ key: 'page', value: parseInt(this.$route.query.page || 1) })

      // Default to Popular
      filterChanges.push({
        key: 'index',
        value: this.$route.query.orderBy || defaultSearchIndex
      })

      this.$store.commit('search/mutateBatch', filterChanges)

      let categories = [this.search.species].filter(category => category !== '')

      if (this.$route.query.breeds) {

        let breed;
        if (this.$route.query.breeds.search('~') !== -1) {
          let strings = this.$route.query.breeds.split('~')

          for (let i in strings) {
            breed = strings[i].replace(/^.+>/, '')
            categories.push(breed.trim().toLowerCase())
          }
        } else {
          breed = this.$route.query.breeds.replace(/^.+>/, '')
          categories.push(breed.trim().toLowerCase())
        }
      }

      if (!categories.length) {
        categories = allSpecies
      }

      // Check route child commodity and map to correct meta Types value. Done for filtering of adverts //
      let metaTypes = []
      let metaSexes = []

      // Do we have good data from the searchpreset API
      if (this.$route.meta.popular && !isEmpty(get(this.searchPresetApi, 'data[0].meta'))) {
        metaTypes = get(this.searchPresetApi, 'data[0].meta.types.value', [])
        metaSexes = get(this.searchPresetApi, 'data[0].meta.sexes.value', [])
      } else {
        if (this.$route.query.types) {
          metaTypes = this.$route.query.types.split('~')
        } else {
          if (this.searchIs.breeding && !this.searchIs.dairy) {
            metaTypes.push('Breeding')
          }

          if (this.searchIs.store) {
            metaTypes.push('Store')
          }

          if (this.searchIs.dairy) {
            metaTypes.push('Dairy')
          }

          if (this.searchIs.teaser) {
            metaTypes.push('Teaser')
          }
        }

        if (this.searchIs.calves) {
          metaSexes.push('Calves')
        }
      }

      let advertConfig = {
        commoditySlug: this.$route.meta.commodity || 'livestock',
        categories,
        displayLocation: displayLocation.SEARCH_PAGE,
        meta: {}
      }

      // API doesn't like having blank arrays being sent
      if (metaTypes.length) {
        advertConfig.meta.types = metaTypes
      }

      if (metaSexes.length) {
        advertConfig.meta.sexes = metaSexes
      }

      this.$store.dispatch('adverts/get', advertConfig)

      await this.fetchListings()

      this.$store.dispatch('search/saveCurrentUrl')
    },

    async fetchListings() {
      this.$store.dispatch('search/fetchListings')
    },

    changePage(pageNumber) {
      this.$analytics.addEvent({
        category: 'search',
        action: 'Changed page',
        label: pageNumber
      })

      this.$store.dispatch('search/changePage', pageNumber)

      this.$scroll.toTop()
    },

    changeHitsPerPage(hitsPerPage) {
      this.$analytics.addEvent({
        category: 'search',
        action: 'Changed hits per page',
        label: hitsPerPage
      })

      this.$store.dispatch('search/changeHitsPerPage', hitsPerPage)
    },

    resetFiltersModal() {
      this.$analytics.addEvent({
        category: 'search',
        action: 'Reset filters'
      })

      this.$store.dispatch('search/reset')
    },

    portalCardClick() {
      this.$analytics.addEvent({
        category: 'search',
        action: 'Clicked portal search card',
        label: this.compMatchingPortal.name
      })

      this.$router.push(this.compMatchingPortal.to)
    }
  },
  head: {
    title() {
      return {
        inner: this.compTitle
      }
    },
    meta() {
      return generateMetaTags({
        title: this.compTitle,
        description: this.$route.meta.description || `Search for ${this.compTitle} on SellMyLivestock`
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.hit-results-wrapper {
  @apply pb-32 relative;
  flex-grow: 1;
  flex-basis: 0; // IE 11 Fix
}

.active-filters::v-deep .tag {
  @apply mx-1;
}

#app.smAndDown .hit-results-wrapper {
  padding-bottom: 10rem;
}

#app.xs .hit-results-wrapper {
  padding-bottom: 12rem;
}

.hit-results {
  @apply flex flex-wrap;
}

.hit-wrapper:not(.hit-wrapper-list) {
  @apply mb-6 w-full;
}

.hit-wrapper-list {
  @apply mb-4 w-full;
}

#app.xs .hit-wrapper-list {
  @apply mb-1;
}

@media (min-width: 579px) {
  .hit-wrapper:not(.hit-wrapper-list) {
    @apply px-2;
    @apply w-6/12;
  }

  .hit-results:not(.hit-results-list) {
    @apply -ml-2 -mr-2;
  }
}

@media (min-width: 1115px) {
  .hit-wrapper:not(.hit-wrapper-list) {
    @apply w-4/12;
  }

  .hit-results:not(.hit-results-list) {
    @apply ml-0 mr-0;
  }
}

@media (min-width: 1440px) {
  .hit-wrapper:not(.hit-wrapper-list) {
    @apply w-3/12;
  }
}
</style>
